import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Gift, PlusCircle, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../lib/apiClient';

type PromotionRow = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  type: 'AMOUNT_OFF' | 'PERCENT_OFF' | 'FREE_SERVICE' | 'BUY_X_GET_Y';
  value: number;
  buyQuantity?: number | null;
  freeQuantity?: number | null;
  appointmentTypeIds: string[];
  locationIds: string[];
  eligibilityRules?: { rewardAppointmentTypeIds?: string[]; minSubtotal?: number; maxRedemptionsPerPatient?: number };
  startsAt: string;
  endsAt: string;
  active: boolean;
  redemptionCount: number;
  discountRedeemed: number;
};

type Option = { id: string; name: string };
type RedemptionRow = {
  id: string;
  code: string;
  promotionName: string;
  patientId?: string | null;
  appointmentId?: string | null;
  locationId?: string | null;
  discountAmount: number;
  redeemedAt: string;
};

const promotionTypes = [
  { value: 'PERCENT_OFF', label: 'Percent off' },
  { value: 'AMOUNT_OFF', label: 'Amount off' },
  { value: 'FREE_SERVICE', label: 'Free service' },
  { value: 'BUY_X_GET_Y', label: 'Buy X get Y' },
] as const;

const blankForm = {
  code: '',
  name: '',
  description: '',
  type: 'PERCENT_OFF',
  value: 0,
  buyQuantity: 4,
  freeQuantity: 1,
  rewardAppointmentTypeIds: [] as string[],
  appointmentTypeIds: [] as string[],
  locationIds: [] as string[],
  minSubtotal: '',
  maxRedemptionsPerPatient: '',
  startsAt: new Date().toISOString().slice(0, 16),
  endsAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 16),
  active: true,
};

function toggleSelection(list: string[], value: string) {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

function serviceScopeLabel(ids: string[] | undefined, serviceNames: Map<string, string>, fallback: string) {
  if (!ids?.length) return fallback;
  return ids.map((id) => serviceNames.get(id) || id).join(', ');
}

function describePromotion(promotion: PromotionRow, serviceNames: Map<string, string>) {
  if (promotion.type === 'BUY_X_GET_Y') {
    const buyLabel = serviceScopeLabel(promotion.appointmentTypeIds, serviceNames, 'any service');
    const freeLabel = serviceScopeLabel(promotion.eligibilityRules?.rewardAppointmentTypeIds, serviceNames, buyLabel);
    return `buy ${promotion.buyQuantity || 1} ${buyLabel}, get ${promotion.freeQuantity || 1} ${freeLabel}`;
  }
  if (promotion.type === 'FREE_SERVICE') {
    return `free ${serviceScopeLabel(promotion.eligibilityRules?.rewardAppointmentTypeIds, serviceNames, 'booked service')}`;
  }
  return promotion.type.replace(/_/g, ' ');
}

function valueLabel(promotion: PromotionRow) {
  if (promotion.type === 'PERCENT_OFF') return `${Number(promotion.value || 0)}% off`;
  if (promotion.type === 'AMOUNT_OFF') return `$${Number(promotion.value || 0).toFixed(2)} off`;
  if (promotion.type === 'FREE_SERVICE') return 'free service';
  if (promotion.type === 'BUY_X_GET_Y') return promotion.value > 0 ? `up to $${Number(promotion.value).toFixed(2)} off` : 'free item value';
  return String(promotion.value || 0);
}

function promotionTypeHelper(type: PromotionRow['type']) {
  if (type === 'PERCENT_OFF') return 'Percent discount applied to the qualifying subtotal.';
  if (type === 'AMOUNT_OFF') return 'Fixed amount removed from the qualifying subtotal.';
  if (type === 'FREE_SERVICE') return 'Discounts one qualifying service to free.';
  return 'Requires qualifying paid quantity before free quantity is discounted.';
}

export default function PromotionsPage() {
  const { fetchJson } = useApiClient();
  const [promotions, setPromotions] = useState<PromotionRow[]>([]);
  const [redemptions, setRedemptions] = useState<RedemptionRow[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [promotionPayload, redemptionPayload, servicePayload, locationPayload] = await Promise.all([
        fetchJson<{ data: PromotionRow[] }>('/promotions'),
        fetchJson<{ data: RedemptionRow[] }>('/promotions/redemptions'),
        fetchJson<Option[]>('/appointment-types'),
        fetchJson<Option[]>('/locations'),
      ]);
      setPromotions(promotionPayload.data || []);
      setRedemptions(redemptionPayload.data || []);
      setServices(servicePayload.map((service) => ({ id: service.id, name: service.name })));
      setLocations(locationPayload.map((location) => ({ id: location.id, name: location.name })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load promotions.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const serviceNames = useMemo(() => new Map(services.map((service) => [service.id, service.name])), [services]);
  const locationNames = useMemo(() => new Map(locations.map((location) => [location.id, location.name])), [locations]);
  const promotionStats = useMemo(() => ({
    active: promotions.filter((promotion) => promotion.active).length,
    scheduled: promotions.filter((promotion) => promotion.active && new Date(promotion.startsAt) > new Date()).length,
    redeemed: promotions.reduce((sum, promotion) => sum + Number(promotion.redemptionCount || 0), 0),
    discount: promotions.reduce((sum, promotion) => sum + Number(promotion.discountRedeemed || 0), 0),
  }), [promotions]);

  async function createPromotion(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const eligibilityRules: { minSubtotal?: number; maxRedemptionsPerPatient?: number; rewardAppointmentTypeIds?: string[] } = {};
      if (form.minSubtotal.trim()) eligibilityRules.minSubtotal = Number(form.minSubtotal);
      if (form.maxRedemptionsPerPatient.trim()) {
        eligibilityRules.maxRedemptionsPerPatient = Number(form.maxRedemptionsPerPatient);
      }
      if (form.type === 'BUY_X_GET_Y' || form.type === 'FREE_SERVICE') {
        eligibilityRules.rewardAppointmentTypeIds = form.rewardAppointmentTypeIds;
      }
      await fetchJson('/promotions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          code: form.code.toUpperCase(),
          buyQuantity: form.type === 'BUY_X_GET_Y' ? form.buyQuantity : undefined,
          freeQuantity: form.type === 'BUY_X_GET_Y' ? form.freeQuantity : undefined,
          eligibilityRules,
          startsAt: new Date(form.startsAt).toISOString(),
          endsAt: new Date(form.endsAt).toISOString(),
        }),
      });
      setForm(blankForm);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save promotion.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Offers &amp; Packages</p>
          <h1 className="text-2xl font-semibold text-slate-900">Offers</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Create discount codes that staff can apply during appointment checkout or invoice editing. Packages are managed separately because they track prepaid balances.</p>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium">
          <RotateCcw size={16} />
          Refresh
        </button>
      </div>

      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <div className="grid gap-3 md:grid-cols-4">
        <OfferMetricCard label="Active offers" value={promotionStats.active} />
        <OfferMetricCard label="Scheduled" value={promotionStats.scheduled} />
        <OfferMetricCard label="Redemptions" value={promotionStats.redeemed} />
        <OfferMetricCard label="Discount redeemed" value={`$${promotionStats.discount.toFixed(2)}`} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <form onSubmit={createPromotion} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <PlusCircle size={18} />
                Create offer
              </div>
              <p className="mt-1 text-sm text-slate-600">Build the code, choose where it can be used, then staff can apply it during checkout.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{form.active ? 'Active on save' : 'Saved inactive'}</span>
          </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Code"><input required value={form.code} onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2 uppercase" /></Field>
          <Field label="Name"><input required value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
          <Field label="Type">
            <select value={form.type} onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value as typeof form.type }))} className="w-full rounded-md border border-slate-300 px-3 py-2">
              {promotionTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </Field>
          <Field label={form.type === 'PERCENT_OFF' ? 'Percent value' : form.type === 'BUY_X_GET_Y' ? 'Maximum discount value' : 'Discount value'} helper={promotionTypeHelper(form.type as PromotionRow['type'])}>
            <input type="number" min={0} step="0.01" value={form.value} onChange={(event) => setForm((prev) => ({ ...prev, value: Math.max(0, Number(event.target.value || 0)) }))} className="w-full rounded-md border border-slate-300 px-3 py-2" />
          </Field>
          <Field label="Starts"><input type="datetime-local" value={form.startsAt} onChange={(event) => setForm((prev) => ({ ...prev, startsAt: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
          <Field label="Ends"><input type="datetime-local" value={form.endsAt} onChange={(event) => setForm((prev) => ({ ...prev, endsAt: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
        </div>
        {form.type === 'BUY_X_GET_Y' ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="grid gap-4 xl:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] xl:items-start">
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <Field label="Buy quantity"><input type="number" min={1} value={form.buyQuantity} onChange={(event) => setForm((prev) => ({ ...prev, buyQuantity: Number(event.target.value) }))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2" /></Field>
                  <Field label="Free quantity"><input type="number" min={1} value={form.freeQuantity} onChange={(event) => setForm((prev) => ({ ...prev, freeQuantity: Number(event.target.value) }))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2" /></Field>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Offer flow</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">Buy {form.buyQuantity || 0}, get {form.freeQuantity || 0} free</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">Choose a free service on the right, or leave it empty to make the reward match the purchased service.</p>
                </div>
              </div>
              <Field label="Free service">
                <SelectionChecklist
                  helper="Choose what the patient receives for free. Leave empty to use the same service as the buy side."
                  options={services}
                  selectedValues={form.rewardAppointmentTypeIds}
                  emptyLabel="Same as selected buy service"
                  onToggle={(value) => setForm((prev) => ({ ...prev, rewardAppointmentTypeIds: toggleSelection(prev.rewardAppointmentTypeIds, value) }))}
                />
              </Field>
            </div>
          </div>
        ) : null}
        {form.type === 'FREE_SERVICE' ? (
          <Field label="Free service">
            <SelectionChecklist
              helper="Choose which service can be discounted to free. Leave empty to discount the booked service."
              options={services}
              selectedValues={form.rewardAppointmentTypeIds}
              emptyLabel="Booked service"
              onToggle={(value) => setForm((prev) => ({ ...prev, rewardAppointmentTypeIds: toggleSelection(prev.rewardAppointmentTypeIds, value) }))}
            />
          </Field>
        ) : null}
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Service scope">
            <SelectionChecklist
              helper="Choose one or more services for this offer. Leave empty to make it available across all services."
              options={services}
              selectedValues={form.appointmentTypeIds}
              emptyLabel="All services"
              onToggle={(value) => setForm((prev) => ({ ...prev, appointmentTypeIds: toggleSelection(prev.appointmentTypeIds, value) }))}
            />
          </Field>
          <Field label="Where offer can be used">
            <SelectionChecklist
              helper="Choose the clinic locations where this offer can be used. Leave empty to use all permitted locations."
              options={locations}
              selectedValues={form.locationIds}
              emptyLabel="All permitted locations"
              onToggle={(value) => setForm((prev) => ({ ...prev, locationIds: toggleSelection(prev.locationIds, value) }))}
            />
          </Field>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3">
            <p className="text-sm font-medium text-slate-900">Offer conditions</p>
            <p className="mt-1 text-sm text-slate-600">These rules control when a patient is allowed to redeem the offer.</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Minimum subtotal">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.minSubtotal}
                onChange={(event) => setForm((prev) => ({ ...prev, minSubtotal: event.target.value }))}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
                placeholder="Optional"
              />
            </Field>
            <Field label="Max uses per patient">
              <input
                type="number"
                min={1}
                step="1"
                value={form.maxRedemptionsPerPatient}
                onChange={(event) => setForm((prev) => ({ ...prev, maxRedemptionsPerPatient: event.target.value }))}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
                placeholder="Optional"
              />
            </Field>
          </div>
        </div>
        <Field label="Description"><textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} className="min-h-20 w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.active} onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))} />
              Active
            </label>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              <Gift size={16} />
              {saving ? 'Saving...' : 'Save offer'}
            </button>
          </div>
        </form>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Draft offer summary</div>
            <div className="mt-4 space-y-3 text-sm">
              <SummaryRow label="Code" value={form.code || 'Not set'} />
              <SummaryRow label="Type" value={promotionTypes.find((entry) => entry.value === form.type)?.label || form.type} />
              <SummaryRow label="Value" value={form.type === 'PERCENT_OFF' ? `${form.value}%` : `$${Number(form.value || 0).toFixed(2)}`} />
              <SummaryRow label="Services" value={form.appointmentTypeIds.length ? `${form.appointmentTypeIds.length} selected` : 'All services'} />
              <SummaryRow label="Locations" value={form.locationIds.length ? `${form.locationIds.length} selected` : 'All permitted'} />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">How staff uses this</div>
            <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
              <p>1. Front desk opens appointment checkout.</p>
              <p>2. Staff enters the offer code in the checkout workspace.</p>
              <p>3. The discount is saved with the payment outcome.</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/offers" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Offers workspace</Link>
              <Link to="/packages" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Packages</Link>
            </div>
          </div>
        </aside>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Offer library</div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Value</th>
              <th className="px-4 py-3">Scope</th>
              <th className="px-4 py-3">Validity</th>
              <th className="px-4 py-3">Redemptions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td className="px-4 py-5 text-slate-500" colSpan={5}>Loading promotions...</td></tr>
            ) : promotions.length ? promotions.map((promotion) => (
              <tr key={promotion.id}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{promotion.code}</div>
                  <div className="text-slate-500">{promotion.name} · {describePromotion(promotion, serviceNames)}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{valueLabel(promotion)}</td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{promotion.appointmentTypeIds.length ? promotion.appointmentTypeIds.map((id) => serviceNames.get(id) || id).join(', ') : 'All services'}</div>
                  <div>{promotion.locationIds.length ? promotion.locationIds.map((id) => locationNames.get(id) || id).join(', ') : 'All permitted locations'}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{new Date(promotion.startsAt).toLocaleDateString()} - {new Date(promotion.endsAt).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-slate-600">{promotion.redemptionCount} uses · ${promotion.discountRedeemed.toFixed(2)}</td>
              </tr>
            )) : (
              <tr><td className="px-4 py-5 text-slate-500" colSpan={5}>No promotions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Recent redemptions</div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Appointment</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Discount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {redemptions.length ? redemptions.map((redemption) => (
              <tr key={redemption.id}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{redemption.code}</div>
                  <div className="text-slate-500">{redemption.promotionName}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{redemption.appointmentId || 'No appointment'}</td>
                <td className="px-4 py-3 text-slate-600">{redemption.locationId ? locationNames.get(redemption.locationId) || redemption.locationId : 'No location'}</td>
                <td className="px-4 py-3 text-slate-600">${Number(redemption.discountAmount || 0).toFixed(2)} · {new Date(redemption.redeemedAt).toLocaleDateString()}</td>
              </tr>
            )) : (
              <tr><td className="px-4 py-5 text-slate-500" colSpan={4}>No redemptions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OfferMetricCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-950">{value}</div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value}</span>
    </div>
  );
}

function Field({ label, children, helper }: { label: string; children: ReactNode; helper?: string }) {
  return (
    <div className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper ? <span className="mt-1 block text-xs leading-5 text-slate-500">{helper}</span> : null}
    </div>
  );
}

function SelectionChecklist({
  options,
  selectedValues,
  onToggle,
  helper,
  emptyLabel,
}: {
  options: Option[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  helper: string;
  emptyLabel: string;
}) {
  return (
    <div className="rounded-md border border-slate-300 bg-white p-3">
      <p className="text-xs text-slate-500">{helper}</p>
      <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
        {selectedValues.length
          ? `${selectedValues.length} selected`
          : emptyLabel}
      </div>
      <div className="mt-3 max-h-44 space-y-2 overflow-y-auto pr-1">
        {options.map((option) => {
          const selected = selectedValues.includes(option.id);
          return (
            <label key={option.id} className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-slate-50">
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggle(option.id)}
                className="mt-0.5"
              />
              <span className={selected ? 'font-medium text-slate-900' : 'text-slate-700'}>{option.name}</span>
            </label>
          );
        })}
        {!options.length ? <p className="px-2 py-1 text-sm text-slate-500">No options available.</p> : null}
      </div>
    </div>
  );
}
